import { InfoOutlineIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Card,
  Circle,
  CircularProgress,
  CircularProgressLabel,
  Grid,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useMediaQuery,
} from "@chakra-ui/react";
import { useState } from "react";
import { customCurrencyFormat, getPixelCount } from "../../utils";

const ProfitCard = ({
  profit = "0.00",
  profitPercentage = "0.00",
  data = {
    caution_percent: "-",
    caution_value: "-",
    neutral_percent: "-",
    neutral_value: "-",
    positive_percent: "-",
    positive_value: "-",
    profit: "-",
    profit_percentage: "-",
    warning_percent: "-",
    warning_value: "-",
  },
  perfectPL = "0.00",
  color = "#458CFF",
  status = false,
  showPL = true,
  dimensions = null,
  circleDimensions = null,
  pdfFormat = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const toggle = () => {
    setIsModalOpen(!isModalOpen);
  };
  const openModal = () => {
    setIsModalOpen(true);
  };

  return (
    <Card
      fontFamily={"lato"}
      width={dimensions ? dimensions : "248px"}
      height={dimensions ? dimensions : "248px"}
      borderRadius="full"
      display="flex"
      alignItems="center"
      justifyContent="center"
      boxShadow={!pdfFormat ? "xl" : "none"}
    >
      {status && (
        <StatusModal
          isOpen={isModalOpen}
          toggle={toggle}
          color={color}
          data={data}
        />
      )}
      <CircularProgress
        value={100}
        size={circleDimensions ? circleDimensions : "268px"}
        thickness="6px"
        color={color}
      >
        <CircularProgressLabel>
          <Box textAlign="center">
            <Box
              fontSize={pdfFormat ? "14px" : "20px"}
              fontWeight="600"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="#6F7D8C"
            >
              Profit
              {status && (
                <Circle
                  cursor="pointer"
                  fontSize="large"
                  color="#6F7D8C"
                  size="16px"
                  bg="white"
                  marginLeft={1}
                  onClick={openModal}
                >
                  <InfoOutlineIcon size={"16px"} />
                </Circle>
              )}
            </Box>
          </Box>
          <Box>
            <Text
              fontSize={getPixelCount(
                pdfFormat ? "37px" : "clamp(32px, calc(3cqw - 0.11vw), 40px)",
                profit,
                8,
                0.2
              )}
              fontWeight="700"
              color={color}
            >
              {profit}
            </Text>
            <Text fontSize={pdfFormat ? "18px" : "22px"} fontWeight="700">
              {profitPercentage}
            </Text>
            {showPL && (
              <Text
                fontSize={pdfFormat ? "12px" : "15px"}
                lineHeight={"18px"}
                fontWeight="600"
                color={"#6F7D8C"}
              >
                Perfect P&L: {perfectPL}
              </Text>
            )}
          </Box>
        </CircularProgressLabel>
      </CircularProgress>
    </Card>
  );
};

export default ProfitCard;

const StatusModal = ({ isOpen, toggle, color, data }) => {
  const [isMobile] = useMediaQuery("(max-width: 767px)");
  const [activeTab, setActiveTab] = useState("status");

  return (
    <Modal isOpen={isOpen} onClose={toggle} isCentered>
      <ModalOverlay />
      <ModalContent
        height={!isMobile ? "auto" : "95%"}
        fontFamily={"lato"}
        maxW={1000}
        width={"95%"}
        padding={"20px 0"}
        borderRadius="12px"
      >
        <ModalHeader
          textAlign={"center"}
          fontSize="20px"
          fontWeight="bold"
          paddingTop={"30px"}
          pb={"2px"}
        >
          <HStack
            size="sm"
            width="100%"
            height={{ base: "auto", md: "48px" }}
            borderRadius="8px"
            overflow="hidden"
            bgColor="#E6E9EC"
            p="6px"
            spacing={1}
            flexDirection={{ base: "column", md: "row" }}
          >
            <Button
              onClick={() => setActiveTab("status")}
              bg={activeTab === "status" ? "white" : "#E6E9EC"}
              color="#262A2C"
              _hover={{ bg: activeTab === "status" ? "white" : "#E6E9EC" }}
              flex="1"
              height="36px"
              borderRadius="8px"
              fontSize={{ base: "15px", md: "16px" }}
              width={{ base: "100%", md: "auto" }}
              minH="36px"
            >
              Your Business Status
            </Button>

            <Button
              onClick={() => setActiveTab("pnl")}
              bg={activeTab === "pnl" ? "white" : "#E6E9EC"}
              color="#262A2C"
              _hover={{ bg: activeTab === "pnl" ? "white" : "#E6E9EC" }}
              flex="1"
              height="36px"
              borderRadius="8px"
              fontSize={{ base: "15px", md: "16px" }}
              width={{ base: "100%", md: "auto" }}
              minH="36px"
            >
              Perfect P&amp;L Framework
            </Button>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody
          display="flex"
          flex={"2 2"}
          flexWrap={"wrap"}
          justifyContent="center"
          overflowY="auto"
          padding={4}
          height={"100%"}
          overflowX={"hidden"}
          sx={{
            "&::-webkit-scrollbar": {
              width: "9px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#30384A",
              borderRadius: "7px",
            },
            "&::-webkit-scrollbar-track": {
              background: "gray.100",
              borderRadius: "4px",
            },
          }}
        >
          {activeTab === "status" ? (
            <Grid
              templateColumns={{
                base: "1fr",
                md: "repeat(2, 1fr)",
                lg: "repeat(4, 1fr)",
              }}
              alignItems="center"
              justifyItems="center"
            >
              <Box
                textAlign={"center"}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
              >
                <Card
                  boxShadow={"xl"}
                  margin={6}
                  marginBottom={3}
                  width={color === "#68BA65" ? "210px" : "200px"}
                  height={color === "#68BA65" ? "210px" : "200px"}
                  borderRadius="100%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  opacity={color === "#68BA65" ? "100%" : "70%"}
                  borderWidth={color === "#68BA65" ? "3px" : "0px"}
                  borderColor={color === "#68BA65" ? "#536DEC" : "black"}
                >
                  <CircularProgress
                    value={100}
                    size={color === "#68BA65" ? "220px" : "210px"}
                    thickness="6px"
                    color="#68BA65"
                  >
                    <CircularProgressLabel>
                      <Box textAlign="center">
                        <Text
                          fontSize="17px"
                          fontWeight="bold"
                          marginBottom={1}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          Positive
                        </Text>
                      </Box>
                      <Box>
                        <Text
                          fontSize={getPixelCount(
                            "35px",
                            customCurrencyFormat(data.positive_value),
                            8,
                            2
                          )}
                          fontWeight="bold"
                          marginBottom={1}
                          color="#68BA65"
                        >
                          {customCurrencyFormat(data.positive_value)}
                        </Text>
                        <Text
                          fontSize="14px"
                          fontWeight="bold"
                          marginBottom={1}
                        >
                          {data.positive_percent !== "-" &&
                          data.positive_percent !== undefined
                            ? `${customCurrencyFormat(
                                data.positive_percent,
                                true
                              )}+`
                            : "-"}
                        </Text>
                      </Box>
                    </CircularProgressLabel>
                  </CircularProgress>
                </Card>
                {color === "#68BA65" ? (
                  <Text color={"#536DEC"} fontSize={"large"} fontWeight={1000}>
                    Your Current Status
                  </Text>
                ) : (
                  <></>
                )}
              </Box>

              <Box
                textAlign={"center"}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
              >
                <Card
                  boxShadow={"xl"}
                  margin={6}
                  marginBottom={3}
                  width={color === "#458CFF" ? "210px" : "200px"}
                  height={color === "#458CFF" ? "210px" : "200px"}
                  borderRadius="100%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  opacity={color === "#458CFF" ? "100%" : "70%"}
                  borderWidth={color === "#458CFF" ? "3px" : "0px"}
                  borderColor={color === "#458CFF" ? "#536DEC" : "black"}
                >
                  <CircularProgress
                    value={100}
                    size={color === "#458CFF" ? "220px" : "210px"}
                    thickness="6px"
                    color="#458CFF"
                  >
                    <CircularProgressLabel>
                      <Box textAlign="center">
                        <Text
                          fontSize="17px"
                          fontWeight="bold"
                          marginBottom={1}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          Neutral
                        </Text>
                      </Box>
                      <Box>
                        <Text
                          fontSize={getPixelCount(
                            "35px",
                            customCurrencyFormat(data.neutral_value),
                            8,
                            2
                          )}
                          fontWeight="bold"
                          marginBottom={1}
                          color="#458CFF"
                        >
                          {customCurrencyFormat(data.neutral_value)}
                        </Text>
                        <Text
                          fontSize="14px"
                          fontWeight="bold"
                          marginBottom={1}
                        >
                          {data.positive_percent !== "-" &&
                          data.warning_percent !== "-" &&
                          data.positive_percent !== undefined &&
                          data.warning_percent !== undefined
                            ? `${customCurrencyFormat(
                                data.positive_percent,
                                true
                              )} -
                        ${customCurrencyFormat(data.warning_percent, true)}`
                            : "-"}
                        </Text>
                      </Box>
                    </CircularProgressLabel>
                  </CircularProgress>
                </Card>
                {color === "#458CFF" ? (
                  <Text color={"#536DEC"} fontSize={"large"} fontWeight={1000}>
                    Your Current Status
                  </Text>
                ) : (
                  <></>
                )}
              </Box>

              <Box
                textAlign={"center"}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
              >
                <Card
                  boxShadow={"xl"}
                  margin={6}
                  marginBottom={3}
                  width={color === "#F1B20E" ? "210px" : "200px"}
                  height={color === "#F1B20E" ? "210px" : "200px"}
                  borderRadius="100%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  opacity={color === "#F1B20E" ? "100%" : "70%"}
                  borderWidth={color === "#F1B20E" ? "3px" : "0px"}
                  borderColor={color === "#F1B20E" ? "#536DEC" : "black"}
                >
                  <CircularProgress
                    value={100}
                    size={color === "#F1B20E" ? "220px" : "210px"}
                    thickness="6px"
                    color="#F1B20E"
                  >
                    <CircularProgressLabel>
                      <Box textAlign="center">
                        <Text
                          fontSize="17px"
                          fontWeight="bold"
                          marginBottom={1}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          Warning
                        </Text>
                      </Box>
                      <Box>
                        <Text
                          fontSize={getPixelCount(
                            "35px",
                            customCurrencyFormat(data.warning_value),
                            8,
                            2
                          )}
                          fontWeight="bold"
                          marginBottom={1}
                          color="#F1B20E"
                        >
                          {customCurrencyFormat(data.warning_value)}
                        </Text>
                        <Text
                          fontSize="14px"
                          fontWeight="bold"
                          marginBottom={1}
                        >
                          {data.warning_percent !== "-" &&
                          data.caution_percent !== "-" &&
                          data.warning_percent !== undefined &&
                          data.caution_percent !== undefined
                            ? `${customCurrencyFormat(
                                data.warning_percent,
                                true
                              )} - ${customCurrencyFormat(
                                data.caution_percent,
                                true
                              )}`
                            : "-"}
                        </Text>
                      </Box>
                    </CircularProgressLabel>
                  </CircularProgress>
                </Card>
                {color === "#F1B20E" ? (
                  <Text color={"#536DEC"} fontSize={"large"} fontWeight={1000}>
                    Your Current Status
                  </Text>
                ) : (
                  <></>
                )}
              </Box>

              <Box
                textAlign={"center"}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
              >
                <Card
                  boxShadow={"xl"}
                  margin={6}
                  marginBottom={3}
                  width={color === "#FF5E5E" ? "210px" : "200px"}
                  height={color === "#FF5E5E" ? "210px" : "200px"}
                  borderRadius="100%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  opacity={color === "#FF5E5E" ? "100%" : "70%"}
                  borderWidth={color === "#FF5E5E" ? "3px" : "0px"}
                  borderColor={color === "#FF5E5E" ? "#536DEC" : "black"}
                >
                  <CircularProgress
                    value={100}
                    size={color === "#FF5E5E" ? "220px" : "210px"}
                    thickness="6px"
                    color="#FF5E5E"
                  >
                    <CircularProgressLabel>
                      <Box textAlign="center">
                        <Text
                          fontSize="17px"
                          fontWeight="bold"
                          marginBottom={1}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          Caution
                        </Text>
                      </Box>
                      <Box>
                        <Text
                          fontSize={getPixelCount(
                            "35px",
                            customCurrencyFormat(data.caution_value),
                            8,
                            2
                          )}
                          fontWeight="bold"
                          marginBottom={1}
                          color="#FF5E5E"
                        >
                          {customCurrencyFormat(data.caution_value)}
                        </Text>
                        <Text
                          fontSize="14px"
                          fontWeight="bold"
                          marginBottom={1}
                        >
                          {data.caution_percent !== "-" &&
                          data.caution_percent !== undefined
                            ? `${customCurrencyFormat(
                                data.caution_percent,
                                true
                              )}-`
                            : "-"}
                        </Text>
                      </Box>
                    </CircularProgressLabel>
                  </CircularProgress>
                </Card>
                {color === "#FF5E5E" ? (
                  <Text color={"#536DEC"} fontSize={"large"} fontWeight={1000}>
                    Your Current Status
                  </Text>
                ) : (
                  <></>
                )}
              </Box>
            </Grid>
          ) : (
            <Box
              mx={2}
              width="100%"
              overflowX="auto"
              borderRadius={"8px"}
              border="1px solid #E2E8F0"
            >
              <Table
                variant="unstyled"
                tableLayout="fixed"
                sx={{
                  "th, td": {
                    border: "1px solid #E2E8F0",
                  },
                }}
              >
                <Thead>
                  <Tr bgColor={"#536DEC"}>
                    <Th
                      colSpan={6}
                      textAlign="center"
                      fontSize="lg"
                      fontWeight="bold"
                      color={"white"}
                      height={"38px"}
                    >
                      Perfect P&amp;L
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr height={"34px"} color={"gray/800"} fontSize={"15px"}>
                    <Td fontSize={"15px"} fontWeight={700}>
                      Monthly Income
                    </Td>
                    <Td textAlign={"right"}>$50,000</Td>
                    <Td textAlign={"right"}>$100,000</Td>
                    <Td textAlign={"right"}>$150,000</Td>
                    <Td textAlign={"right"}>$250,000</Td>
                    <Td textAlign={"right"}>$500,000</Td>
                  </Tr>
                  <Tr height={"34px"} color={"gray/800"} fontSize={"15px"}>
                    <Td fontSize={"15px"} fontWeight={700}>
                      COGS / Products
                    </Td>
                    <Td textAlign={"right"}>0% - 20%</Td>
                    <Td textAlign={"right"}>0% - 20%</Td>
                    <Td textAlign={"right"}>0% - 20%</Td>
                    <Td textAlign={"right"}>0% - 20%</Td>
                    <Td textAlign={"right"}>0% - 20%</Td>
                  </Tr>
                  <Tr
                    height={"34px"}
                    color={"#536DEC"}
                    fontSize={"15px"}
                    fontWeight={700}
                  >
                    <Td fontSize={"15px"} fontWeight={700}>
                      Gross Profit
                    </Td>
                    <Td textAlign={"right"}>80% - 100%</Td>
                    <Td textAlign={"right"}>80% - 100%</Td>
                    <Td textAlign={"right"}>80% - 100%</Td>
                    <Td textAlign={"right"}>80% - 100%</Td>
                    <Td textAlign={"right"}>80% - 100%</Td>
                  </Tr>
                  <Tr height={"34px"} color={"gray/800"} fontSize={"15px"}>
                    <Td fontSize={"15px"} fontWeight={700}>
                      Marketing
                    </Td>
                    <Td textAlign={"right"}>8% - 12%</Td>
                    <Td textAlign={"right"}>10% - 14%</Td>
                    <Td textAlign={"right"}>12% - 16%</Td>
                    <Td textAlign={"right"}>14% - 18%</Td>
                    <Td textAlign={"right"}>15% - 20%</Td>
                  </Tr>
                  <Tr height={"34px"} color={"gray/800"} fontSize={"15px"}>
                    <Td fontSize={"15px"} fontWeight={700}>
                      Team
                    </Td>
                    <Td textAlign={"right"}>15% - 25%</Td>
                    <Td textAlign={"right"}>18% - 30%</Td>
                    <Td textAlign={"right"}>20% - 35%</Td>
                    <Td textAlign={"right"}>24% - 40%</Td>
                    <Td textAlign={"right"}>30% - 45%</Td>
                  </Tr>
                  <Tr height={"34px"} color={"gray/800"} fontSize={"15px"}>
                    <Td fontSize={"15px"} fontWeight={700}>
                      Operations
                    </Td>
                    <Td textAlign={"right"}>22% - 30%</Td>
                    <Td textAlign={"right"}>18% - 26%</Td>
                    <Td textAlign={"right"}>15% - 24%</Td>
                    <Td textAlign={"right"}>12% - 20%</Td>
                    <Td textAlign={"right"}>10% - 15%</Td>
                  </Tr>
                  <Tr height={"34px"} color={"gray/800"} fontSize={"15px"}>
                    <Td pl={"30px"} fontSize={"15px"} fontWeight={700}>
                      Practice Costs
                    </Td>
                    <Td textAlign={"right"}>6% - 8%</Td>
                    <Td textAlign={"right"}>5% - 7%</Td>
                    <Td textAlign={"right"}>4% - 6%</Td>
                    <Td textAlign={"right"}>3% - 5%</Td>
                    <Td textAlign={"right"}>2% - 5%</Td>
                  </Tr>
                  <Tr height={"34px"} color={"gray/800"} fontSize={"15px"}>
                    <Td pl={"30px"} fontSize={"15px"} fontWeight={700}>
                      Overhead
                    </Td>
                    <Td textAlign={"right"}>13% - 18%</Td>
                    <Td textAlign={"right"}>12% - 16%</Td>
                    <Td textAlign={"right"}>10% - 14%</Td>
                    <Td textAlign={"right"}>8% - 12%</Td>
                    <Td textAlign={"right"}>7% - 10%</Td>
                  </Tr>
                  <Tr height={"34px"} color={"gray/800"} fontSize={"15px"}>
                    <Td pl={"30px"} fontSize={"15px"} fontWeight={700}>
                      Business Development
                    </Td>
                    <Td textAlign={"right"}>1% - 4%</Td>
                    <Td textAlign={"right"}>1% - 4%</Td>
                    <Td textAlign={"right"}>1% - 3%</Td>
                    <Td textAlign={"right"}>1% - 2%</Td>
                    <Td textAlign={"right"}>1%</Td>
                  </Tr>
                  <Tr
                    height={"34px"}
                    color={"#536DEC"}
                    fontSize={"15px"}
                    fontWeight={700}
                  >
                    <Td fontSize={"15px"} fontWeight={700}>
                      Profit
                    </Td>
                    <Td textAlign={"right"}>32% - 40%</Td>
                    <Td textAlign={"right"}>30% - 37%</Td>
                    <Td textAlign={"right"}>26% - 33%</Td>
                    <Td textAlign={"right"}>23% - 27%</Td>
                    <Td textAlign={"right"}>22% - 25%</Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
